import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { CloseIcon, DeleteIcon, FileIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseImage, BaseRow, BaseUploadFile } from '~/components/common';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '~/redux/hooks';

import { IJobType, IJobTypeCreate } from '~/utils/interface/jobType';
import { UploadFile, UploadFileWithNote } from '~/utils/types/common';
import { FileTypeEnum } from '~/utils/constants/file';
import { handleCreateJobType, handleEditJobType } from '~/thunks/job-type/jobTypeThunk';
import { jobTypeActions } from '~/thunks/job-type/jobTypeSlice';

type EditJobTypeDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  title?: string;
  data?: IJobType;
  children?: React.ReactNode;
};

const initialValues: IJobTypeCreate = {
  name: '',
  code: '',
};

const EditJobTypeDialog = (props: EditJobTypeDialogProps) => {
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          setDisable(true);
          await dispatch(handleCreateJobType(values)).unwrap();
          dispatch(jobTypeActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          setUploadFiles([]);
          handleSetOpen(false, true);
          setDisable(false);
        } catch (e: unknown) {
          setDisable(false);
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          setDisable(true);
          await dispatch(handleEditJobType({ id: props?.data?.id ?? '', params: values })).unwrap();
          dispatch(jobTypeActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          setUploadFiles([]);
          handleSetOpen(false, true);
          setDisable(false);
        } catch (e: unknown) {
          setDisable(false);
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  //#region Destructuring Props
  const { mode = DIALOG_MODE_CREATE } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job-type']);
  const dispatch = useAppDispatch();

  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [uploadFiles, setUploadFiles] = useState<Array<UploadFileWithNote>>([]);
  const [disable, setDisable] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(props.data?.name));
      form.setFieldValue('code', cast(props.data?.code));
      form.setFieldValue('filesUpload', cast(props.data?.jobTypeFileIds));

      const filesUpload = (props.data?.jobTypeFileIds ?? []).map((file: any) => ({
        name: file?.name ? file?.name.split('.')[0] : file?.name,
        note: file.note && file.note !== 'null' ? file.note : '',
        fileExtension: file?.originalUrl ? file.originalUrl.split('.').pop() ?? '' : '',
        fileId: file.fileId,
        url: file?.url,
        type: file?.type,
      }));
      setUploadFiles(filesUpload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, props.data]);

  //#endregion Implement Hook

  //#region Handle Function

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const handleCancel = () => {
    handleSetOpen(false);
    setUploadFiles([]);
    form.resetForm();
  };

  const handleUploadFile = (newFiles: UploadFile[]) => {
    let newFilesObject: Array<UploadFileWithNote> = [];
    // eslint-disable-next-line array-callback-return
    newFiles.map((file, index) => {
      newFilesObject[index] = {
        name: file.file?.name ? file.file.name.split('.')[0] : '',
        url: file.url ?? (file.file ? URL.createObjectURL(file.file) : ''),
        type: file.file?.type ? (file.file.type.includes('image') ? FileTypeEnum.IMAGE : FileTypeEnum.ANY) : '',
        fileExtension: file.file?.name ? file.file.name.split('.')[1] : '',
        file: file.file,
      };
    });
    setUploadFiles([...uploadFiles, ...newFilesObject]);
    const filesUpload = newFiles.map((file) => ({
      type: file.file?.type ? (file.file.type.includes('image') ? FileTypeEnum.IMAGE : FileTypeEnum.ANY) : '',
    }));
    if (mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('files', [...uploadFiles, ...newFilesObject]);
      form.setFieldValue('filesUpload', filesUpload);
    } else {
      form.setFieldValue('files', [...newFilesObject]);
    }
  };
  const handleDeleteImage = (index: number) => {
    const newFiles = uploadFiles.filter((obj, key) => {
      return key !== index;
    });
    setUploadFiles(newFiles);
    form.setFieldValue('files', newFiles);
    form.setFieldValue('filesUpload', newFiles);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const key = Number(name.split('.')[1]);
    if (name.includes('name') && uploadFiles[key]) uploadFiles[key].name = value ?? ' ';
    if (name.includes('note') && uploadFiles[key]) uploadFiles[key].note = value ?? ' ';
    setUploadFiles([...uploadFiles]);
    const filesUpload = uploadFiles.map((file) => ({
      name: file.name ?? ' ',
      note: file.note ?? ' ',
      type: file?.type,
    }));

    const filesUploadEdit = uploadFiles.map((file) => ({
      fileId: file.fileId ?? '',
      name: `${file.name}.${file.fileExtension}`,
      note: file.note && file.note !== 'null' ? file.note : '',
      type: file?.type,
    }));

    if (mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('filesUpload', filesUpload);
    } else {
      form.setFieldValue('filesUpload', filesUploadEdit);
    }
  };
  //#endregion Handle Function

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        handleSetOpen(false);
        setUploadFiles([]);
      }}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNew') : t('edit')}
        <IconButton
          onClick={() => {
            handleSetOpen(false);
            setUploadFiles([]);
          }}
          className='absolute right-[10px] top-[10px]'
        >
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel required>{t('properties.name')}</InputLabel>
              <TextField
                name='name'
                required
                placeholder={t('properties.name')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel required>{t('properties.code')}</InputLabel>
              <TextField
                name='code'
                required
                placeholder={t('properties.code')}
                value={form.values?.code}
                onChange={form.handleChange}
                error={Boolean(form.errors?.code)}
                helperText={form.errors?.code}
              />
            </Box>
          </BaseRow>

          <Box>
            <InputLabel required>{t('properties.templateFiles')}</InputLabel>
            <Box component='form'>
              <BaseUploadFile
                isSubmitonDrop={true}
                isMultiple={true}
                onChange={(files: Array<UploadFile>) => handleUploadFile(files)}
              />
            </Box>
            <Box className='w-full'>
              {uploadFiles?.map((file, index) => (
                <Box key={index} className='border-2 border-solid my-2 relative'>
                  <IconButton className='absolute right-0 top-0' onClick={() => handleDeleteImage(index)}>
                    <DeleteIcon className='w-[12px] h-[12px]' />
                  </IconButton>
                  <Box className='flex justify-flex-start w-full'>
                    <input type='file' hidden name={`files.${index}.file`} />
                    <Box onClick={() => window.open(file?.url)} className='w-[100px] h-[100px]'>
                      {file?.type === FileTypeEnum.IMAGE ? (
                        <BaseImage src={file?.url} className='w-full h-full object-cover' />
                      ) : (
                        <FileIcon className='mx-auto my-[15px] w-[70px] h-[70px]' />
                      )}
                    </Box>
                    <Box className='block border-l-2 px-[50px] flex-1'>
                      <Box className='flex mt-3 w-full'>
                        <InputLabel className='w-[100px] content-center	'> {t('fileItem.name')}</InputLabel>
                        <TextField
                          name={`files.${index}.name`}
                          data-key={index}
                          value={cast(file.name ?? '')}
                          onChange={handleChange}
                          InputProps={{
                            endAdornment: <InputAdornment position='end'>.{file.fileExtension}</InputAdornment>,
                          }}
                        />
                      </Box>
                      <Box className='flex mt-3 w-full'>
                        <InputLabel className='w-[100px] content-center'>{t('fileItem.note')}</InputLabel>
                        <TextField
                          name={`files.${index}.note`}
                          data-key={index}
                          value={cast(file.note ?? '')}
                          type='text'
                          onChange={handleChange}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' disabled={disable} onClick={() => form.handleSubmit()}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditJobTypeDialog);
